import {storeToRefs} from "pinia";
import type {Ref} from "vue";
import type {CalendarOptions, ClassNameInput} from "@fullcalendar/vue3";
import {joinClassNames} from "@fullcalendar/vue3";
import formaThemePlugin from "@fullcalendar/vue3/themes/forma";
import {useNamaFlagsStore} from "@/stores/nama-flags-store";

import "@fullcalendar/vue3/skeleton.css";
import "@fullcalendar/vue3/themes/forma/theme.css";
import "@/css/fullcalendar-forma.scss";

export {formaThemePlugin};

export function useCalendarDarkMode(): Ref<boolean> {
  const {darkMode} = storeToRefs(useNamaFlagsStore());
  return darkMode;
}

export function formaCalendarOptions(options: CalendarOptions, isDark: boolean): CalendarOptions {
  return {
    ...options,
    plugins: [formaThemePlugin, ...(options.plugins ?? [])],
    colorScheme: isDark ? "dark" : "light"
  };
}

/**
 * Adds page-specific classes to one of the theme's class-name options. Setting such an option
 * directly would replace the Forma classes for that element, so the theme's own value is
 * resolved first and the extra classes are appended to it.
 */
export function withFormaClass<T>(optionName: keyof CalendarOptions,
                                  extraClass: (info: T) => ClassNameInput): (info: T) => string {
  const themeClass = (formaThemePlugin as any).optionDefaults?.[optionName];
  return (info: T) => joinClassNames(
    typeof themeClass === "function" ? themeClass(info) : themeClass, extraClass(info));
}

/**
 * Value for a `*Content` option whose markup is supplied by a Vue slot. FullCalendar renders
 * the option's content generator and teleports the slot into the same element, so a theme that
 * ships its own generator (Forma renders day headers) would draw its markup next to the slot's.
 * Null or undefined fall back to the theme, hence a function that renders nothing.
 */
export function contentFromSlot(): null {
  return null;
}
